import { ReactElement, useCallback, useEffect, useState } from 'react';
import { useOutletContext, useParams } from 'react-router-dom';

import VisibilityOff from '@mui/icons-material/VisibilityOffOutlined';
import Visibility from '@mui/icons-material/VisibilityOutlined';
import {
  Button,
  Chip,
  IconButton,
  Stack,
  styled,
  Box,
  Tooltip,
} from '@mui/material';
import moment from 'moment';

import { MasterIndexApi, MasterIndexApiPath, MasterNameApi } from '@/api';
import { FlagsApi } from '@/api/api.flags';
import { IcShieldPerson } from '@/assets/images';
import {
  IViewCard,
  IViewItem,
  FileGrid,
  FormCard,
  ViewItem,
  FlagChips,
  PageHeader,
} from '@/components';
import CopyableWrapper from '@/components/ClickToCopy/ViewItemWithCopy';
import { SocialMediaPlatforms } from '@/components/CustomForms/SocialMediaForm/data';
import NavigationButtonGroup from '@/components/NavigationButtonGroup/NavigationButtonGroup';
import { VerifiedBadge } from '@/components/VerifiedBadge';
import { DATE_FORMAT } from '@/config';
import { USStates } from '@/constants';
import {
  EthnicOriginList,
  Gender,
  LanguageList,
  NationalityList,
  RaceList,
  GenderList,
} from '@/constants/form_inputs';
import {
  DisabilitiesCodes,
  EyeDefectsCodes,
  FaceCodes,
  FacialFeaturesCodes,
  HairTypesCodes,
} from '@/constants/users';
import { useFormConfigLoader, useKaseContext } from '@/hooks';
import { TabAnimationWrapper, useTabLayoutContext } from '@/layouts/TabLayout';
import { EmergencyContactsTable } from '@/layouts/TabLayout/MasterNames/components/EmergencyContactsTable';
import {
  AddressStatus,
  IMasterName,
  IMasterNamePerson,
  IOffenseOptionItem,
  IPhoneNumber,
  ISocialMedia,
} from '@/models';
import { getOffenseOptionItems } from '@/pages/Kases/KaseCrimes/utilities';
import {
  MasterIndexDetailsContext,
  MasterIndexType,
} from '@/pages/MasterIndex/components/MasterIndexDetailsPage';
import MasterIndexRelatedRecords from '@/pages/MasterIndex/components/MasterIndexRelatedRecords/MasterIndexRelatedRecords';
import { SexOffenderBanner } from '@/pages/MasterIndex/Persons/components/SexOffenderBanner';
import MasterIndexPersonFlags from '@/pages/MasterIndex/Persons/Flags';
import { MasterIndexPersonNotes } from '@/pages/MasterIndex/Persons/Notes';
import { getAddressLocation } from '@/services';
import { useToastStore } from '@/store';
import {
  convertToAsteriskSSN,
  flattenObject,
  formatCountryDisplay,
  formatEducationDisplay,
  formatEyeColorDisplay,
  formatFullSSN,
  formatHairColorDisplay,
  formatHeightDisplay,
  formatMaritalStatusDisplay,
  formatPersonName,
  formatPhoneNumber,
  formatReligionDisplay,
  generateUniqueId,
} from '@/utils';

import { useMasterNamesContext } from '..';
import {
  MasterNameViewFields,
  TabNamesNextPaths,
  TabNamesScreens,
} from '../data';

const LinkButton = styled(Button)(({ theme }) => ({
  textDecoration: 'underline',
  color: theme.palette.error.main,
  fontWeight: 'normal',
}));

interface TabCounts {
  relatedRecords: number;
  flags: number;
  notes: number;
}

type MasterNameViewProps = {
  hideHeader?: boolean;
  onGoBack?: () => void;
  isMasterIndex?: boolean;
};

const EmployeeBadge = () => (
  <Box
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: 28,
      height: 28,
      borderRadius: 1,
      backgroundColor: 'error.main',
    }}
  >
    <IcShieldPerson style={{ width: 18, height: 18, color: '#fff' }} />
  </Box>
);

export const MasterNameView = ({
  hideHeader,
  isMasterIndex,
  ...props
}: MasterNameViewProps) => {
  const outletContext = useOutletContext<
    MasterIndexDetailsContext | undefined
  >();
  const setCustomTitle = outletContext?.setCustomTitle;
  const setLastUpdatedAt = outletContext?.setLastUpdatedAt;
  const { updateToast } = useToastStore();
  const { currentMastername, linkedMasterNames, setCurrentMastername, source } =
    useMasterNamesContext();
  const { isReadOnlyNames } = useKaseContext();
  const [currentName, setCurrentName] = useState<IMasterName>();
  const { updateCurrentScreen } = useTabLayoutContext();

  const [showSSN, setShowSSN] = useState(false);
  const [fullSSN, setFullSSN] = useState<string | null>(null);
  const [loadingSSN, setLoadingSSN] = useState(false);

  // State to track active tab - defaults to 'information'
  const [activeTab, setActiveTab] = useState('information');
  const [tabCounts, setTabCounts] = useState<TabCounts>({
    relatedRecords: 0,
    flags: 0,
    notes: 0,
  });

  const { id: personId } = useParams();

  const { filteredOptions: filteredMasterNameOptions } = useFormConfigLoader({
    optionsName: 'USER',
    category: 'USER',
  });

  const relationship = linkedMasterNames.find(
    (item) => item.masterName?._id === currentMastername?.masterName._id,
  )?.relationship;

  const flattenNameObj = flattenObject({
    ...currentName,
    relationship,
  });

  const FORM_TO_VIEW_FIELD_MAP: Record<string, string> = {
    firstName: 'firstName',
    lastName: 'lastName',
    middleName: 'middleName',
    suffix: 'suffix',
    birthday: 'birthday',
    sex: 'sex',
    'physicalCharacteristics.eyeColor': 'physicalCharacteristics.eyeColor',
    'physicalCharacteristics.heightExact.feet':
      'physicalCharacteristics.heightExact.feet',
    'physicalCharacteristics.heightExact.inches':
      'physicalCharacteristics.heightExact.inches',
    'driverLicense.state': 'driverLicense.state',
    'driverLicense.number': 'driverLicense.number',
    'driverLicense.vehicleClass': 'driverLicense.vehicleClass',
    'driverLicense.restrictionCodes': 'driverLicense.restrictionCodes',
    'driverLicense.endorsementCodes': 'driverLicense.endorsementCodes',
    'driverLicense.expireDate': 'driverLicense.expireDate',
    'driverLicense.documentNumber': 'driverLicense.documentNumber',
    'driverLicense.issueDate': 'driverLicense.issueDate',
    'addresses.0.addressNumber': 'addresses.0.addressNumber',
    'addresses.0.completeStreetName': 'addresses.0.completeStreetName',
    'addresses.0.subAddress': 'addresses.0.subAddress',
    'addresses.0.placeName': 'addresses.0.placeName',
    'addresses.0.state': 'addresses.0.state',
    'addresses.0.zipcode': 'addresses.0.zipcode',
    'addresses.0.country': 'addresses.0.country',
  };

  const isFieldVerified = useCallback(
    (viewFieldPath: string): boolean => {
      const idScanVerifiedFields = (currentName as IMasterNamePerson)
        ?.idScanVerifiedFields;
      if (!idScanVerifiedFields) return false;

      const formFieldPath = Object.entries(FORM_TO_VIEW_FIELD_MAP).find(
        ([, viewPath]) => viewPath === viewFieldPath,
      )?.[0];

      if (!formFieldPath) return false;

      const scannedValue = idScanVerifiedFields[formFieldPath];
      if (scannedValue === undefined) return false;

      const currentValue = flattenNameObj[viewFieldPath];

      const normalizeValue = (val: any): string => {
        if (val === null || val === undefined || val === '') return '';
        return String(val).trim().toUpperCase();
      };

      return normalizeValue(currentValue) === normalizeValue(scannedValue);
    },
    [currentName, flattenNameObj],
  );

  const fetchTabCounts = useCallback(async (id: string) => {
    if (!id) return;

    try {
      const [relatedRes, flagsRes, notesRes] = await Promise.all([
        MasterIndexApi.getMasterRelatedRecords(MasterIndexApiPath.People, id, {
          sort: JSON.stringify({ createdAt: -1 }),
        }),
        FlagsApi.getPersonFlags(id, { limit: 1 }),
        MasterNameApi.getPaginatedMasterNameNotes(id, { limit: 1 }),
      ]);

      setTabCounts({
        relatedRecords:
          relatedRes.data?.totalDocs || relatedRes.data?.docs?.length || 0,
        flags: Array.isArray(flagsRes.data) ? flagsRes.data.length : 0,
        notes: notesRes.data?.totalDocs || 0,
      });
    } catch {
      // Silently fail - counts are non-critical
    }
  }, []);

  useEffect(() => {
    if (currentMastername?.masterName._id) {
      fetchCurrentMastername();
      fetchTabCounts(currentMastername.masterName._id);
    }
  }, [currentMastername?.masterName._id]);

  useEffect(() => {
    if (personId) {
      fetchCurrentMastername();
      fetchTabCounts(personId);
    }
  }, [personId]);

  const fetchCurrentMastername = async () => {
    try {
      const res = personId
        ? await MasterNameApi.getMasterName(personId)
        : await MasterNameApi.getMasterName(
            String(currentMastername?.masterName._id),
          );
      setCurrentName(res.data);
      if (setCustomTitle && res.data.firstName && res.data.lastName) {
        const title = res.data.uniqueId
          ? `${generateUniqueId('PER', res.data.uniqueId)} - ${formatPersonName(
              res.data,
            )}`
          : formatPersonName(res.data);
        setCustomTitle(title);
      }
      setLastUpdatedAt?.(res.data?.updatedAt);
      setCurrentMastername({
        masterName: res.data,
        relationship: {},
        kaseId: '',
        createdAt: new Date(),
        updatedAt: new Date(),
      });
    } catch (err: any) {
      const status = err?.response?.status ?? err?.statusCode ?? err?.status;
      const isForbidden =
        status === 403 ||
        /permission to access this resource/i.test(err?.message || '');
      if (isForbidden && currentMastername?.masterName) {
        setCurrentName(currentMastername.masterName);
      } else {
        updateToast({ open: true, message: err.message });
      }
    }
  };

  const onGoBack = () => {
    if (props.onGoBack) {
      return props.onGoBack();
    }
    updateCurrentScreen(TabNamesScreens.NamesList);
  };

  const handleEdit = () => {
    if (currentMastername) {
      setCurrentMastername(currentMastername);
      updateCurrentScreen(TabNamesScreens.MasterAddNameDetails);
    }
  };

  useEffect(() => {
    setShowSSN(false);
    setFullSSN(null);
  }, [currentMastername?.masterName._id, personId]);

  const fetchFullSSN = async (): Promise<string | null> => {
    const id = String(currentMastername?.masterName._id || personId || '');
    if (!id) return null;
    setLoadingSSN(true);
    try {
      const res = await MasterNameApi.getMasterNameSSN(id);
      setFullSSN(res.data.ssn);
      return res.data.ssn;
    } catch (err: any) {
      updateToast({ open: true, message: err.message });
      return null;
    } finally {
      setLoadingSSN(false);
    }
  };

  const handleToggleSSN = async () => {
    if (showSSN) {
      setShowSSN(false);
      return;
    }
    const ssn = fullSSN ?? (await fetchFullSSN());
    if (ssn) setShowSSN(true);
  };

  const getFullSSNValue = async (): Promise<string> => {
    return fullSSN ?? (await fetchFullSSN()) ?? '';
  };

  const renderViewCardContent = ({ key, items, size }: IViewCard) => {
    if (key === 'photos') {
      return (
        <ViewItem sx={{ mt: -4 }} md={8}>
          <FileGrid files={flattenNameObj.files} readOnly={isReadOnlyNames} />
        </ViewItem>
      );
    }

    if (key === 'emergencyContacts') {
      return (
        <EmergencyContactsTable
          emergencyContacts={currentName?.emergencyContacts ?? []}
          items={items}
          showMapsIcon={true}
        />
      );
    }

    if (key === 'verificationStatus') {
      return (
        <ViewItem
          md={2}
          label="Verification Status"
          value={currentName?.isVerified ? 'Verified' : 'Not Verified'}
          copyable
        />
      );
    }

    return items.map((item: IViewItem & { method?: any }) => {
      let value;
      if (item && item?.method && item.method in linkRenders) {
        value = linkRenders[item.method](flattenNameObj[item.value as string]);
      } else if (item.value === 'personalInfo.addresses') {
        const getAddressString = (addr: any): string => {
          if (!addr) return '';
          // Build full address from components
          const streetPart = [addr.addressNumber, addr.completeStreetName]
            .filter(Boolean)
            .join(' ');
          const parts = [
            streetPart,
            addr.subAddress,
            addr.placeName,
            addr.state,
            addr.zipcode,
          ].filter(Boolean);
          if (parts.length > 0) {
            return parts.join(', ');
          }
          if (addr.addressLabel) {
            return addr.addressLabel;
          }
          if (addr.normalized) {
            return addr.normalized;
          }
          return '';
        };

        const isAddressVerified = (): boolean => {
          const idScanVerifiedFields = (currentName as IMasterNamePerson)
            ?.idScanVerifiedFields;
          if (!idScanVerifiedFields) return false;

          const addresses = (currentName as IMasterNamePerson)?.addresses;
          if (!addresses || addresses.length === 0) return false;

          const firstAddress = addresses[0];
          const normalizeValue = (val: any): string => {
            if (val === null || val === undefined || val === '') return '';
            return String(val).trim().toUpperCase();
          };

          const scannedAddressNumber =
            idScanVerifiedFields['addresses.0.addressNumber'];
          const scannedStreetName =
            idScanVerifiedFields['addresses.0.completeStreetName'];

          const addressNumberMatches =
            scannedAddressNumber !== undefined &&
            normalizeValue(firstAddress.addressNumber) ===
              normalizeValue(scannedAddressNumber);
          const streetNameMatches =
            scannedStreetName !== undefined &&
            normalizeValue(firstAddress.completeStreetName) ===
              normalizeValue(scannedStreetName);

          return addressNumberMatches || streetNameMatches;
        };

        const singleAddress = flattenNameObj['address.addressLabel']
          ? {
              addressLabel: flattenNameObj['address.addressLabel'],
              status: flattenNameObj['address.status'],
            }
          : null;

        const addressList = flattenNameObj.addresses?.length
          ? flattenNameObj.addresses
          : singleAddress
          ? [singleAddress]
          : [];

        const addressVerified = isAddressVerified();

        return (
          <ViewItem key={item.value} label="Address and status" md={4}>
            <Stack spacing={1} sx={{ width: '100%', mt: 1 }}>
              {addressList.map((address: any, index: number) => {
                const addressString = getAddressString(address);
                const addressValue = addressString
                  ? `${addressString} - ${
                      address.status === AddressStatus.ACTIVE
                        ? 'Active'
                        : 'Inactive'
                    }`
                  : '';

                return (
                  <Box
                    key={`address-${index}`}
                    sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                  >
                    <CopyableWrapper
                      label=""
                      value={addressValue}
                      showMapsIcon={true}
                    />
                    {index === 0 && addressVerified && (
                      <VerifiedBadge tooltip="Verified by state ID" />
                    )}
                  </Box>
                );
              })}
            </Stack>
          </ViewItem>
        );
      } else if (
        item.value === 'driverLicense.address.addressLabel' ||
        item.value === 'employerInfo.address.addressLabel'
      ) {
        let fullAddress;
        switch (item.value) {
          case 'driverLicense.address.addressLabel':
            fullAddress = (currentName as IMasterNamePerson)?.driverLicense
              ?.address;
            break;
          default:
            fullAddress = (currentName as IMasterNamePerson)?.employerInfo
              ?.address;
        }
        value = getAddressLocation(fullAddress ?? undefined);
        return (
          <ViewItem key={item.label} md={4} {...item} value={value} copyable />
        );
      } else if (
        item.value === 'birthday' ||
        item.value === 'driverLicense.birthday'
      ) {
        const birthday = flattenNameObj.birthday;
        const currentAge = birthday
          ? moment().diff(moment(birthday), 'years')
          : null;
        value = birthday
          ? moment(birthday).format(DATE_FORMAT) + ` (${currentAge})`
          : '';
      } else if (
        item.value === 'driverLicense.expireDate' ||
        item.value === 'driverLicense.issueDate' ||
        item.value === 'driverLicense.revisionDate'
      ) {
        const dateValue = flattenNameObj[item.value];
        value = dateValue ? moment(dateValue).format(DATE_FORMAT) : '';
      } else if (item.value === 'ssn') {
        const rawSSN = flattenNameObj[item.value as string];
        const displaySSN =
          showSSN && fullSSN
            ? formatFullSSN(fullSSN)
            : convertToAsteriskSSN(rawSSN);
        const toggleButtonSx = {
          width: 32,
          height: 32,
          borderRadius: '6px',
          border: '1px solid',
          borderColor: 'divider',
          backgroundColor: 'background.paper',
          color: 'text.secondary',
        };
        return (
          <ViewItem
            key={item.label}
            label={item.label}
            md={12}
            {...size}
            copyable
            value={displaySSN}
            getCopyValue={getFullSSNValue}
            persistentActions={
              rawSSN ? (
                showSSN ? (
                  <Tooltip title="Hide SSN" placement="top">
                    <IconButton
                      size="small"
                      onClick={handleToggleSSN}
                      disabled={loadingSSN}
                      sx={toggleButtonSx}
                    >
                      <VisibilityOff fontSize="small" />
                    </IconButton>
                  </Tooltip>
                ) : (
                  <Tooltip title="Show SSN" placement="top">
                    <IconButton
                      size="small"
                      onClick={handleToggleSSN}
                      disabled={loadingSSN}
                      sx={toggleButtonSx}
                    >
                      <Visibility fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )
              ) : undefined
            }
          />
        );
      } else if (item.value === 'sex') {
        value =
          flattenNameObj[item.value as string] != Gender.UNKNOWN
            ? GenderList[
                flattenNameObj[item.value as string] as keyof typeof GenderList
              ]
            : '';
      } else if (item.value === 'race') {
        value =
          RaceList[
            flattenNameObj[item.value as string] as keyof typeof RaceList
          ];
      } else if (item.value === 'ethnicOrigin') {
        value =
          EthnicOriginList[
            flattenNameObj[
              item.value as string
            ] as keyof typeof EthnicOriginList
          ];
      } else if (item.value === 'nationality') {
        const currentOptionItem = getOffenseOptionItems(
          item.value,
          filteredMasterNameOptions,
        ) as Map<string, IOffenseOptionItem>;

        const itemValue = flattenNameObj[item.value];
        const foundOption = Array.isArray(currentOptionItem)
          ? currentOptionItem.find((it) => it.value === itemValue)
          : null;
        value =
          foundOption?.label ||
          NationalityList[itemValue as keyof typeof NationalityList] ||
          itemValue;
      } else if (item.value === 'maritalStatus') {
        value = formatMaritalStatusDisplay(flattenNameObj.maritalStatus);
      } else if (item.value === 'religion') {
        value = formatReligionDisplay(flattenNameObj.religion);
      } else if (item.value === 'education') {
        value = formatEducationDisplay(flattenNameObj.education);
      } else if (item.value === 'citizenOf') {
        value = formatCountryDisplay(flattenNameObj.citizenOf);
      } else if (item.value === 'physicalCharacteristics.hairColor') {
        value = formatHairColorDisplay(flattenNameObj[item.value]);
      } else if (item.value === 'spokenLanguages') {
        const languagesText =
          flattenNameObj?.spokenLanguages
            ?.map(
              (language: keyof typeof LanguageList) => LanguageList[language],
            )
            .join(', ') ?? '';

        return (
          <ViewItem
            key={item.value}
            label="Languages spoken"
            md={2}
            value={languagesText}
            copyable
          />
        );
      } else if (item.value === 'physicalCharacteristics.disabilities') {
        const text =
          flattenNameObj['physicalCharacteristics.disabilities']
            ?.map((v: keyof typeof DisabilitiesCodes) => {
              return DisabilitiesCodes[v];
            })
            .join(', ') ?? '';

        return (
          <ViewItem
            key={item.value}
            label="Disabilities"
            md={2}
            value={text}
            copyable
          />
        );
      } else if (item.value === 'physicalCharacteristics.eyeColor') {
        const eyeColorVerified = isFieldVerified(
          'physicalCharacteristics.eyeColor',
        );
        return (
          <ViewItem
            key={item.value}
            label="Eye color"
            md={2}
            value={formatEyeColorDisplay(flattenNameObj[item.value])}
            copyable
            endAdornment={
              eyeColorVerified ? (
                <VerifiedBadge tooltip="Verified by state ID" />
              ) : undefined
            }
          />
        );
      } else if (item.value === 'physicalCharacteristics.heightExact') {
        const heightVerified =
          isFieldVerified('physicalCharacteristics.heightExact.feet') &&
          isFieldVerified('physicalCharacteristics.heightExact.inches');
        return (
          <ViewItem
            key={item.value}
            label="Height"
            md={2}
            value={formatHeightDisplay(
              (currentName as IMasterNamePerson)?.physicalCharacteristics
                ?.heightExact,
            )}
            copyable
            endAdornment={
              heightVerified ? (
                <VerifiedBadge tooltip="Verified by state ID" />
              ) : undefined
            }
          />
        );
      } else if (item.value === 'physicalCharacteristics.eyeDefects') {
        const text =
          flattenNameObj['physicalCharacteristics.eyeDefects']
            ?.map((v: keyof typeof EyeDefectsCodes) => {
              return EyeDefectsCodes[v];
            })
            .join(', ') ?? '';

        return (
          <ViewItem
            key={item.value}
            label="Eye defects"
            md={2}
            value={text}
            copyable
          />
        );
      } else if (item.value === 'physicalCharacteristics.face') {
        const text =
          flattenNameObj['physicalCharacteristics.face']
            ?.map((v: keyof typeof FaceCodes) => {
              return FaceCodes[v];
            })
            .join(', ') ?? '';

        return (
          <ViewItem
            key={item.value}
            label="Face"
            md={2}
            value={text}
            copyable
          />
        );
      } else if (item.value === 'physicalCharacteristics.facialFeatures') {
        const text =
          flattenNameObj['physicalCharacteristics.facialFeatures']
            ?.map((v: keyof typeof FacialFeaturesCodes) => {
              return FacialFeaturesCodes[v];
            })
            .join(', ') ?? '';

        return (
          <ViewItem
            key={item.value}
            label="Facial features"
            md={2}
            value={text}
            copyable
          />
        );
      } else if (item.value === 'physicalCharacteristics.hairTypes') {
        const text =
          flattenNameObj['physicalCharacteristics.hairTypes']
            ?.map((v: keyof typeof HairTypesCodes) => {
              return HairTypesCodes[v];
            })
            .join(', ') ?? '';

        return (
          <ViewItem
            key={item.value}
            label="Hair types"
            md={2}
            value={text}
            copyable
          />
        );
      } else if (item.value === 'socialMedia') {
        const socialMediaText =
          flattenNameObj.socialMedia
            ?.map(
              ({ platform, username }: ISocialMedia) =>
                `${
                  SocialMediaPlatforms[
                    platform as keyof typeof SocialMediaPlatforms
                  ]
                } - ${username}`,
            )
            .join(',\n') ?? '';

        return (
          <ViewItem
            key={item.value}
            label="Social media"
            md={2}
            value={socialMediaText}
            valueStyle={{ whiteSpace: 'pre-line' }}
            copyable
          />
        );
      } else if (item.value === 'emails') {
        const emailsText = flattenNameObj.emails?.join('\n') ?? '';

        return (
          <ViewItem
            key={item.value}
            label="Emails"
            md={2}
            value={emailsText}
            copyable
          />
        );
      } else if (item.value === 'driverLicense.state') {
        const val = flattenNameObj['driverLicense.state'];
        const currentStateLabel = USStates.find((state) => state.value === val);

        value = currentStateLabel?.label || '';
      } else if (item.value === 'driverLicense.name') {
        value = formatPersonName({
          firstName: flattenNameObj['driverLicense.firstName'],
          middleName: flattenNameObj['driverLicense.middleName'],
          lastName: flattenNameObj['driverLicense.lastName'],
        });
      } else if (item.value === 'transient' || item.value === 'foreigner') {
        const boolValue = flattenNameObj[item.value as string];
        return (
          <ViewItem
            key={item.value}
            label={item.label}
            md={2}
            value={boolValue ? 'Yes' : 'No'}
            copyable
          />
        );
      } else if (item.value === 'personalInfo.phoneNumbers') {
        return flattenNameObj.phoneNumbers?.map((phoneItem: IPhoneNumber) => {
          const formatted = formatPhoneNumber(phoneItem.phoneNumber);
          return (
            <ViewItem
              key={phoneItem.type}
              label={`Phone number (${phoneItem.type})`}
              md={2}
              value={
                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                  }}
                >
                  {formatted}
                  {phoneItem.isPrimary && (
                    <Chip
                      size="small"
                      color="error"
                      label="Primary"
                      sx={{ height: 20, fontSize: '0.7rem', fontWeight: 600 }}
                    />
                  )}
                </span>
              }
              copyable
              getCopyValue={async () => formatted}
            />
          );
        });
      } else if (item.value === 'employerInfo.phoneNumber') {
        return (
          <ViewItem
            key={item.label}
            label={item.label}
            md={2}
            value={formatPhoneNumber(
              flattenNameObj['employerInfo.phoneNumber'],
            )}
            copyable
          />
        );
      } else {
        value = flattenNameObj[item.value as string];
      }

      const fieldPath = item.value as string;
      const showVerifiedBadge = isFieldVerified(fieldPath);

      return (
        <ViewItem
          key={item.label}
          md={12}
          {...item}
          {...size}
          value={value}
          copyable
          endAdornment={
            showVerifiedBadge ? (
              <VerifiedBadge tooltip="Verified by state ID" />
            ) : undefined
          }
        />
      );
    });
  };

  const linkRenders: {
    [key: string]: (value?: string) => ReactElement;
  } = {
    viewAllNotes: () => (
      <LinkButton
        onClick={() => updateCurrentScreen(TabNamesScreens.MasterNameNotes)}
      >
        View all notes
        <Chip color="error" size="small" label="3" sx={{ ml: 1 }} />
      </LinkButton>
    ),
    KasesInvolvementHistory: () => (
      <LinkButton
        onClick={() =>
          updateCurrentScreen(TabNamesScreens.KasesInvolvementHistory)
        }
      >
        View all cases
        <Chip color="error" size="small" label="3" sx={{ ml: 1 }} />
      </LinkButton>
    ),
    viewCfsHistory: () => (
      <LinkButton
        onClick={() =>
          updateCurrentScreen(TabNamesScreens.CFSInvolvementHistory)
        }
      >
        View all CFS&apos;s
        <Chip
          color="error"
          size="small"
          label={(currentName as IMasterNamePerson)?.linkedCFSs || 0}
          sx={{ ml: 1, aspectRatio: 1, '& .MuiChip-label': { p: 0 } }}
        />
      </LinkButton>
    ),
    checkIsVerified: (currentValue) => {
      return <>{currentValue ? 'Verified' : 'Not Verified'}</>;
    },
  };

  const getTabs = (
    type: MasterIndexType,
  ): { label: string; path: string; disabled?: boolean; count?: number }[] => {
    switch (type) {
      case 'persons':
        return [
          {
            label: 'Personal information',
            path: 'information',
          },
          {
            label: 'Related records',
            path: 'related',
            count: tabCounts.relatedRecords,
          },
          {
            label: 'Flags',
            path: 'flags',
            count: tabCounts.flags,
          },
          {
            label: 'Notes',
            path: 'notes',
            count: tabCounts.notes,
          },
        ];
      default:
        return [];
    }
  };

  const tabs = getTabs('persons');

  // Render content based on active tab
  const renderTabContent = () => {
    switch (activeTab) {
      case 'information':
        return (
          <Stack
            overflow="auto"
            sx={{
              '.p-kase-edit &': { p: 2.5, borderRadius: 2 },
            }}
          >
            {MasterNameViewFields.map((data) => (
              <FormCard
                className="grey-title"
                key={data.key}
                title={data.title}
              >
                {renderViewCardContent(data)}
              </FormCard>
            ))}
          </Stack>
        );

      case 'related':
        return (
          <MasterIndexRelatedRecords
            type={MasterIndexApiPath.People}
            viewId={currentMastername?.masterName._id}
          />
        );

      case 'flags':
        return (
          <MasterIndexPersonFlags
            viewId={currentMastername?.masterName._id}
            readOnly={isReadOnlyNames}
            onFlagsUpdate={() => {
              fetchCurrentMastername();
              fetchTabCounts(
                currentMastername?.masterName._id || personId || '',
              );
            }}
          />
        );

      case 'notes':
        return (
          <MasterIndexPersonNotes
            viewId={currentMastername?.masterName._id}
            readOnly={isReadOnlyNames}
            onCountChange={() =>
              fetchTabCounts(
                currentMastername?.masterName._id || personId || '',
              )
            }
          />
        );

      default:
        return null;
    }
  };

  // Updated tabs with onClick handlers
  const tabsWithHandlers = tabs.map((tab) => ({
    ...tab,
    onClick: () => setActiveTab(tab.path),
  }));

  const getPersonTitle = () => {
    const personName = currentName as IMasterNamePerson;

    if (personName?.firstName && personName?.lastName) {
      if (currentName?.uniqueId && !isMasterIndex) {
        return `${generateUniqueId(
          'PER',
          currentName.uniqueId,
        )} - ${formatPersonName(personName)}`;
      }
      return formatPersonName(personName);
    }

    return undefined;
  };

  const renderTitle = () => {
    const title = getPersonTitle();
    const personName = currentName as IMasterNamePerson;
    const showEmployeeBadge = personName?.isEmployee;

    // Add proper checks for flags
    if (personName?.flags && personName.flags.length > 0) {
      return (
        <Stack direction="row" alignItems="center" spacing={2}>
          <span>{title}</span>
          {showEmployeeBadge && <EmployeeBadge />}
          <FlagChips
            flags={personName.flags.map((f) => f.flag)}
            size="medium"
            animateImportant
          />
        </Stack>
      );
    }

    if (showEmployeeBadge) {
      return (
        <Stack direction="row" alignItems="center" spacing={2}>
          <span>{title}</span>
          <EmployeeBadge />
        </Stack>
      );
    }

    return title;
  };

  const renderActionButtons = () => {
    if (activeTab !== 'information') {
      return null;
    }

    const isKaseContext = source === 'kase';

    return (
      <Stack direction="row" spacing={2}>
        {!isMasterIndex && (
          <Button
            variant="outlined"
            onClick={() =>
              updateCurrentScreen(
                isKaseContext
                  ? TabNamesScreens.MasterNameKaseInvolvement
                  : TabNamesScreens.MasterNameSetInvolvement,
              )
            }
            sx={{
              color: 'text.primary',
              borderColor: 'divider',
              '&:hover': {
                borderColor: 'divider',
                backgroundColor: 'action.hover',
              },
            }}
          >
            Involvement
          </Button>
        )}
        {!isReadOnlyNames && (
          <Button color="error" variant="contained" onClick={handleEdit}>
            Edit
          </Button>
        )}
      </Stack>
    );
  };

  return (
    <TabAnimationWrapper nextPaths={TabNamesNextPaths.MasterNameView}>
      <div className="bg-[#F5F7F7] dark:bg-gray-800 flex flex-col h-full overflow-hidden text-gray-900 dark:text-gray-100">
        {!hideHeader && (
          <div className="flex-shrink-0 p-2">
            <PageHeader
              title={renderTitle()}
              onBack={onGoBack}
              action={!isMasterIndex ? renderActionButtons() : undefined}
            />
          </div>
        )}

        {isMasterIndex && personId && <SexOffenderBanner personId={personId} />}

        <div className="!w-full px-2">
          {!hideHeader && tabs && (
            <NavigationButtonGroup
              tabs={tabsWithHandlers}
              activeTab={activeTab}
            />
          )}
        </div>

        <div className="flex-1 overflow-auto p-2 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
          {renderTabContent()}
        </div>
      </div>
    </TabAnimationWrapper>
  );
};
